import { MessagePrimitive } from '@assistant-ui/react';
import { Accordion } from '@mantine/core';
import { motion, stagger, type Variants } from 'motion/react';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '#core/contexts/ProjectContext.ts';

import classes from './CollapsibleToolRun.module.css';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';

interface CollapsibleToolRunProps {
  indices: readonly number[];
}

/**
 * Renders a run of consecutive collapsible tool-call parts.
 *
 * Layout:
 * - Summary toggle: "Show N tools" / "Hide N tools" (pill button).
 * - Collapse panel: each tool's normal render via `MessagePrimitive.PartByIndex`
 *   (falling back to `ToolDisplay` — same as today's rendering).
 * - Progress bubble: rendered below the toggle whenever the run's last part is
 *   still running (result === undefined). Shown in both collapsed and expanded
 *   states to keep the "AiPSO Agent is ..." indicator visible while work
 *   continues.
 */
export function CollapsibleToolRun({ indices }: CollapsibleToolRunProps) {
  const { t } = useTranslation('chat');
  const { config } = use(ProjectContext);
  const toolCount = indices.length;
  const [value, setValue] = useState<string | null>(null);
  return (
    <div className={classes.toolRun}>
      <Accordion
        chevronPosition="left"
        classNames={{ label: classes.accordionLabel, content: classes.accordionContent }}
        onChange={setValue}
        transitionDuration={0}
        value={value}
        variant="unstyled"
      >
        <Accordion.Item bg="transparent" value="tool-run">
          <Accordion.Control c="dark.2" className={classes.toolRunToggle} fz="sm">
            {t('toolRunSummary', { count: toolCount, agent: config.feature.ai.agent.name })}
          </Accordion.Control>
          <Accordion.Panel>
            {value === 'tool-run' && (
              <motion.div animate="visible" className={classes.content} initial="hidden" variants={variants}>
                {indices.map((index) => (
                  <motion.div key={index} variants={childVariants}>
                    <MessagePrimitive.PartByIndex components={components} index={index} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}

const components = { tools: { Fallback: ToolDisplay } };

const variants: Variants = {
  hidden: {
    opacity: 0,
    height: 0,
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.2,
      delayChildren: stagger(0.1),
    },
  },
};

const childVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.2,
    },
  },
};
